import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactElement } from "react";
import { FRAMES } from "odyssey-shared";
import { centreOffset, logical } from "../../ui/kit/index.ts";
import { classes } from "../../ui/kit/classes.ts";
import { useInteraction } from "../../ui/kit/interaction.tsx";
import { buttonFrame, frameClass } from "../../ui/kit/state.ts";
import { ENTRY_ROW, pickerKey } from "./picker.ts";
import { scopeIconClass, type ChatScope } from "./scope-icon.tsx";
import type { ScopeTab } from "./tabs.ts";
import { pickerName } from "./words.ts";
import { say } from "../../words.ts";

/**
 * The chat entry row's scope picker (P1.9-T40, ADR-068's amendment "the entry row as redrawn, and the scope picker";
 * the design's picker, a visual reference): a 22 by 16 button showing the scope the draft goes to, its icon and a
 * caret, which opens a list of the open scopes over the row. The button is named for where the draft goes; the list
 * is a listbox the button owns (`aria-controls`, `aria-activedescendant`), so the button keeps the focus and
 * assistive technology reads each scope as the highlight reaches it. Enter and Space open the list from the focused
 * button; while it shows, Up, Down, Home and End move the highlight, Enter and Space choose, and Tab closes it on its
 * way out, the only keys it takes (`pickerKey`); Escape closes it through the input router's layer. A press of the
 * pointer on the button or a scope keeps focus where it was, so the field keeps the keyboard while a scope is
 * picked, and a press anywhere else closes the list, as do sending a line and the field losing focus, so the list
 * and its layer never outlive what they were opened for.
 */

/**
 * The list's element id, which the button names.
 */
export const PICKER_ID = "ot-chat-picker";

/**
 * The side of a scope icon as the atlas draws it: its 8 px glyph inside a pixel of outline.
 */
const ICON = 10;

/**
 * The pixel between the icon and the caret, the design's.
 */
const CARET_GAP = 1;

/**
 * Where the icon and the caret sit in the button, from the inside of its frame's edge: the pair set in the middle of
 * the button across, each in the middle of it down, on whole logical pixels.
 */
const PICKER_PARTS = pickerParts();

/**
 * Lays the icon and the caret out in the button.
 *
 * @returns The icon's and the caret's left and top from the inside of the frame's edge.
 */
function pickerParts(): { readonly icon: { readonly left: number; readonly top: number }; readonly caret: { readonly left: number; readonly top: number } } {
  const insets = FRAMES.button.insets;
  const left = centreOffset(ENTRY_ROW.pickerWidth, ICON + CARET_GAP + ENTRY_ROW.caret.width) - insets.left;
  return {
    icon: { left, top: centreOffset(ENTRY_ROW.height, ICON) - insets.top },
    caret: { left: left + ICON + CARET_GAP, top: centreOffset(ENTRY_ROW.height, ENTRY_ROW.caret.height) - insets.top },
  };
}

/**
 * A scope's element id in the list, which the button names while it is highlighted.
 *
 * @param scope The scope.
 * @returns The id.
 */
export function pickerOptionId(scope: ScopeTab): string {
  return `${PICKER_ID}-${scope}`;
}

/**
 * Keeps focus where it was when the picker is pressed with the pointer; the click still acts.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * The picker.
 *
 * @param props The `scope` the draft goes to, or null for a command, which shows the caret alone; the `scopes` the
 *   list offers; `onPick` with a scope chosen; whether the picker is `disabled`; the row's `bottom` edge above the
 *   list in logical px, so the list sits over the row; `layer`, which holds the input router's layer while the
 *   list shows and is told when Escape closes it; and `dismissed`, a count the panel raises to close the list, as a
 *   line is sent and as the field loses focus.
 * @returns The button, and the list while it shows.
 */
export function ScopePicker({ scope, scopes, onPick, disabled, bottom, layer, dismissed }: { readonly scope: ChatScope | null; readonly scopes: readonly ScopeTab[]; readonly onPick: (scope: ScopeTab) => void; readonly disabled: boolean; readonly bottom: number; readonly layer: (shown: boolean, onDismiss: () => void) => void; readonly dismissed: number }): ReactElement {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<ScopeTab>("local");
  const root = useRef<HTMLSpanElement>(null);
  const spaceChose = useRef(false);
  const { state, handlers } = useInteraction({ disabled, focus: "visible" });
  const shown = open && !disabled && scopes.length > 0;
  const lit = scopes.includes(highlight) ? highlight : scopes[0];
  useEffect(() => {
    layer(shown, () => setOpen(false));
  }, [shown]);
  useEffect(() => {
    setOpen(false);
  }, [dismissed]);
  useEffect(() => {
    if (!shown) {
      return undefined;
    }
    /**
     * Closes the list when the pointer goes down outside the picker.
     *
     * @param event The press.
     */
    const onPress = (event: PointerEvent): void => {
      if (event.target instanceof Node && root.current?.contains(event.target) !== true) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPress, true);
    return () => document.removeEventListener("pointerdown", onPress, true);
  }, [shown]);
  /**
   * Shows or hides the list, the highlight starting on the scope the draft goes to.
   */
  const toggle = (): void => {
    setHighlight(scopes.find((entry) => entry === scope) ?? scopes[0] ?? "local");
    setOpen((was) => !was);
  };
  /**
   * Chooses a scope and closes the list.
   *
   * @param chosen The scope.
   */
  const choose = (chosen: ScopeTab): void => {
    setOpen(false);
    onPick(chosen);
  };
  /**
   * The list's keys while it shows; a key it answers is prevented and goes no further, so neither the input router
   * nor the game acts on it. Tab closes the list and still moves focus.
   *
   * @param event The key press on the button.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (!shown || lit === undefined || event.altKey || event.ctrlKey || event.metaKey) {
      handlers.onKeyDown(event);
      return;
    }
    const move = pickerKey(scopes, lit, event.key);
    if (move === null) {
      return;
    }
    if (move.kind === "close") {
      setOpen(false);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (move.kind === "move") {
      setHighlight(move.to);
    } else {
      spaceChose.current = event.key === " ";
      choose(move.scope);
    }
  };
  /**
   * Space going up on the button after it chose a scope: prevented, so the click a browser makes as Space comes up
   * does not open the list the key just chose from.
   *
   * @param event The key release on the button.
   */
  const onKeyUp = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (spaceChose.current && event.key === " ") {
      spaceChose.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    handlers.onKeyUp(event);
  };
  const frame = buttonFrame("secondary", { ...state, disabled, selected: false });
  const down = state.pressed ? 1 : 0;
  return (
    <span ref={root} className="ot-chat__pick" data-chat="scope">
      <button
        type="button"
        className={classes("ot-button", "ot-chat__picker", frameClass(frame), state.hover && "is-hover", state.pressed && "is-pressed", state.focus && "is-focus", disabled && "is-disabled")}
        data-chat="picker"
        data-chat-picked={scope ?? ""}
        disabled={disabled}
        aria-label={pickerName(scope === null ? null : say(`chat.scopes.${scope}`))}
        aria-haspopup="listbox"
        aria-expanded={shown}
        aria-controls={shown ? PICKER_ID : undefined}
        aria-activedescendant={shown && lit !== undefined ? pickerOptionId(lit) : undefined}
        onClick={toggle}
        onMouseDown={keepFocus}
        style={{ width: logical(ENTRY_ROW.pickerWidth), height: logical(ENTRY_ROW.height) }}
        {...handlers}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={(event) => {
          handlers.onBlur(event);
          setOpen(false);
        }}
      >
        {scope === null ? null : <span className={`ot-chat__picker-icon ${scopeIconClass(scope)}`} aria-hidden="true" style={{ left: logical(PICKER_PARTS.icon.left), top: logical(PICKER_PARTS.icon.top + down) }} />}
        <span className="ot-chat__picker-caret" aria-hidden="true" style={{ left: logical(PICKER_PARTS.caret.left), top: logical(PICKER_PARTS.caret.top + down), width: logical(ENTRY_ROW.caret.width), height: logical(ENTRY_ROW.caret.height) }} />
      </button>
      {shown ? (
        <div className="ot-chat__picker-list ot-ui-frame-panel" data-chat="picker-list" style={{ bottom: logical(bottom) }} onMouseDown={keepFocus}>
          <ul className="ot-chat__options" id={PICKER_ID} role="listbox" aria-label={say("chat.pickerList")}>
            {scopes.map((entry) => (
              <li key={entry} id={pickerOptionId(entry)} className="ot-chat__option ot-chat__picker-option" role="option" aria-selected={entry === scope} data-chat="pick" data-chat-pick={entry} data-chat-lit={entry === lit ? "true" : undefined} onClick={() => choose(entry)} onPointerMove={() => setHighlight(entry)}>
                <span className={`ot-chat__picker-mark ${scopeIconClass(entry)}`} aria-hidden="true" />
                <span>{say(`chat.scopes.${entry}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </span>
  );
}
